// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Interfaces
import {
  IMyDocumentDownloadDocumentAttrs,
  IMyDocumentPreviewDocumentAttrs,
  IMyDocumentGetAllAttrs,
  IMyDocumentGetAttrs,
  IMyDocumentSaveAttrs,
  IMyDocumentValidateAttrs,
  IMyDocumentDeleteAttrs
} from 'interfaces/back-office/personal-data/my-document/my-document-view.interface'

const useMyDocument = () => {
  // Hook
  const {
    myDocument: {
      myDocument_isLoading,
      myDocument_isSaveLoading,
      myDocument_isDeleteLoading,
      myDocument_myDocuments,
      myDocument_detail,
      myDocument_validation,
      myDocument_isValidationLoading,
      myDocument_CLEAR_MY_DOCUMENTS,
      myDocument_CLEAR_MY_DOCUMENT,
      myDocument_CLEAR_VALIDATION,
      myDocument_fetchMyDocuments: myDocumentFetchList,
      myDocument_fetchMyDocumentsByUserId: myDocumentFetchListByUserId,
      myDocument_fetchMyDocument: myDocumentFetch,
      myDocument_saveMyDocument: myDocumentSave,
      myDocument_downloadDocument: myDocumentDownloadDocument,
      myDocument_previewDocumentByUserId: myDocumentPreviewDocumentByUserId,
      myDocument_validate: myDocumentValidate,
      myDocument_deleteMyDocument: myDocumentDelete
    },
  } = useStores()

  /**
   * @description Fetch document list
   *
   * @param {IMyDocumentGetAllAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const myDocument_fetchMyDocuments = useCallback((payload: IMyDocumentGetAllAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      myDocumentFetchList({
        body: payload.body,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Fetch document list for a spesific user (used in user detail)
   *
   * @param {IMyDocumentGetAllAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const myDocument_fetchMyDocumentsByUserId = useCallback((payload: IMyDocumentGetAllAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      myDocumentFetchListByUserId({
        body: payload.body,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Fetch document detail
   *
   * @param {IMyDocumentGetAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const myDocument_fetchMyDocument = useCallback((payload: IMyDocumentGetAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      myDocumentFetch({
        params: payload.params,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Save document
   *
   * @param {IMyDocumentSaveAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const myDocument_saveMyDocument = useCallback((payload: IMyDocumentSaveAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      myDocumentSave({
        params: payload.params,
        body: payload.body,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Download document
   *
   * @param {IMyDocumentDownloadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const myDocument_downloadDocument = useCallback((payload: IMyDocumentDownloadDocumentAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      myDocumentDownloadDocument({
        params: payload.params,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Preview or Download document given User ID and Blob ID
   *
   * @param {IMyDocumentPreviewDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const myDocument_previewDocumentByUserId = useCallback((payload: IMyDocumentPreviewDocumentAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      myDocumentPreviewDocumentByUserId({
        params: payload.params,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  /**
   * @description Validate
   *
   * @param {IMyDocumentValidateAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const myDocument_validate = useCallback(
    (payload: IMyDocumentValidateAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        myDocumentValidate({
          body: payload.body,
          requestConfig: payload.requestConfig,
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    // eslint-disable-next-line
    [],
  )

  /**
   * @description Delete document
   *
   * @param {IMyDocumentDeleteAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const myDocument_deleteMyDocument = useCallback((payload: IMyDocumentDeleteAttrs): Promise<void> => {
    return new Promise((resolve, reject) => {
      myDocumentDelete({
        params: payload.params,
        requestConfig: payload.requestConfig,
        onSuccess: resolve,
        onError: reject,
      })
    })

    // eslint-disable-next-line
  }, [])

  return {
    myDocument_isLoading,
    myDocument_isSaveLoading,
    myDocument_isDeleteLoading,
    myDocument_myDocuments,
    myDocument_detail,
    myDocument_validation,
    myDocument_isValidationLoading,
    myDocument_CLEAR_MY_DOCUMENTS,
    myDocument_CLEAR_MY_DOCUMENT,
    myDocument_CLEAR_VALIDATION,
    myDocument_fetchMyDocuments,
    myDocument_fetchMyDocumentsByUserId,
    myDocument_fetchMyDocument,
    myDocument_saveMyDocument,
    myDocument_downloadDocument,
    myDocument_previewDocumentByUserId,
    myDocument_validate,
    myDocument_deleteMyDocument
  }
}

export { useMyDocument }
